import { useMemo, useState, useEffect, useRef } from "react";
import { Sparkles, Clock, Lock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { billingCheckout, trackEvent } from "@/lib/api";
import { detectBillingCurrency } from "@/lib/billing";

/**
 * FlipCost trial banner.
 *
 * Surfaces three things on a trial project:
 *   1) Days remaining (or "Expired" once past the 7-day window).
 *   2) The 1-room cap reminder.
 *   3) The unlock CTA — kicks off Paystack checkout for the Single Project
 *      plan in the visitor's detected currency. Backend webhook will then
 *      auto-promote this exact project (clear `trial`) on success.
 *
 * Renders inline at the top of the dashboard. The banner is purely
 * informational — actual enforcement (room cap + expiry) happens server-side
 * in /api/projects PUT.
 */
export default function TrialBanner({ project }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Days remaining — null if no expiry timestamp set (shouldn't happen on a
  // trial project but we defend anyway).
  const { daysLeft, expired, price } = useMemo(() => {
    let _daysLeft = null;
    let _expired = false;
    if (project?.trial_expires_at) {
      const exp = new Date(project.trial_expires_at);
      const diffMs = exp.getTime() - Date.now();
      _daysLeft = Math.max(0, Math.ceil(diffMs / 86400000));
      _expired = diffMs <= 0;
    }
    const cur = detectBillingCurrency();
    const _price = cur === "ZAR" ? "R280" : "$19";
    return { daysLeft: _daysLeft, expired: _expired, price: _price };
  }, [project?.trial_expires_at]);

  // Fire impression once per project. Lives above the early-return so the
  // hook order stays stable. Skips the ping for non-trial projects.
  const firedFor = useRef(null);
  useEffect(() => {
    if (!project?.trial) return;
    if (firedFor.current === project.id) return;
    firedFor.current = project.id;
    trackEvent("trial_banner_impression", `project:${project.id}`, {
      project_id: project.id,
      expired,
      days_left: daysLeft,
    });
  }, [project?.trial, project?.id, expired, daysLeft]);

  if (!project?.trial) return null;

  const startCheckout = async () => {
    trackEvent("trial_unlock_clicked", "banner:trial", {
      project_id: project.id,
      expired,
      days_left: daysLeft,
    });
    setBusy(true);
    try {
      const callback_url = `${window.location.origin}/billing/callback`;
      const { authorization_url } = await billingCheckout({
        plan: "single",
        currency: detectBillingCurrency(),
        callback_url,
      });
      window.location.href = authorization_url;
    } catch (err) {
      // 503 = Paystack disabled in this env. Send them to the Pricing
      // notify-me block instead of leaving them stranded.
      if (err?.response?.status === 503) {
        toast.error("Checkout isn't live in this environment yet.");
        navigate("/pricing");
      } else {
        toast.error("Could not start checkout — please try again.");
      }
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-5" data-testid="trial-banner">
      <div
        className={`rounded-lg border px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
          expired
            ? "bg-amber-50 border-amber-200 text-amber-900"
            : "bg-sage-50 border-sage-200 text-sage-800"
        }`}
      >
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-medium rounded-full px-2.5 py-1 border self-start sm:self-auto ${
            expired
              ? "bg-amber-100 border-amber-200 text-amber-900"
              : "bg-sage-100 border-sage-200 text-sage-700"
          }`}
        >
          {expired ? (
            <>
              <Lock className="w-3 h-3" /> Trial ended
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3" /> Free trial
            </>
          )}
        </span>

        <div className="text-sm leading-relaxed flex-1 min-w-0">
          {expired ? (
            <>
              <strong>Your 7-day FlipCost trial has ended.</strong> Unlock the
              full project to add more rooms, extensions, exterior and soft
              costs — and keep editing.
            </>
          ) : (
            <>
              <strong className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {daysLeft === null
                  ? "Trial active"
                  : daysLeft === 0
                  ? "Less than a day left"
                  : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your trial`}
              </strong>
              {" — "}your trial includes 1 room. Unlock the full project for{" "}
              <span className="fc-mono">{price}</span> to plan the rest of the
              property (rooms, extensions, exterior, soft costs).
            </>
          )}
        </div>

        <button
          type="button"
          onClick={startCheckout}
          disabled={busy}
          data-testid="trial-unlock-btn"
          className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-sage-700 hover:bg-sage-800 text-white text-sm font-medium rounded-md h-10 px-4 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? "Starting…" : `Unlock for ${price}`}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
